import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import type { AgentInput, LanguageDefinitionResponse, TemplateAutocomplete } from '@onefootprint/request-types/dashboard';
import { Button, Form, IconButton, Toggle } from '@onefootprint/ui';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CelNodeFormSubtitle } from '@/components/playbook-builder/components/shared/cel-node-form-instructions';
import ExpandableExpressionEditor from '@/components/playbook-builder/components/shared/expandable-expression-editor';
import { schemaTypeLabel, TypeChip } from '@/features/agent-builder';
import type { AiDecisionFormData } from '../../types';
type InputDataProps = {
  canEdit: boolean;
  autocompleteItems?: TemplateAutocomplete;
  languageDefinition?: LanguageDefinitionResponse;
  agentManaged: boolean;
  declaredInputs?: AgentInput[];
  vaultDocumentAccess?: {
    enabled: boolean;
    available: boolean;
  };
};
const InputData = t0 => {
  const $ = _c(57);
  const {
    canEdit,
    autocompleteItems,
    languageDefinition,
    agentManaged,
    declaredInputs,
    vaultDocumentAccess
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "aiDecisionNode.inputData"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    control
  } = useFormContext();
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: "inputs"
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t2);
  let t3;
  if ($[3] !== control) {
    t3 = {
      control,
      name: "inputs"
    };
    $[3] = control;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const watchedInputs = useWatch(t3);
  const canEditInputSchema = !!canEdit && !agentManaged;
  let handleAddInput;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== agentManaged || $[6] !== append || $[7] !== autocompleteItems || $[8] !== canEdit || $[9] !== canEditInputSchema || $[10] !== control || $[11] !== declaredInputs || $[12] !== fields || $[13] !== languageDefinition || $[14] !== remove || $[15] !== t || $[16] !== vaultDocumentAccess || $[17] !== watchedInputs) {
    const declaredByKey = new Map((declaredInputs ?? []).map(_temp));
    let t10;
    if ($[25] !== append) {
      t10 = () => {
        append({
          key: "",
          comment: "",
          expression: ""
        });
      };
      $[25] = append;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    handleAddInput = t10;
    t8 = "flex flex-col gap-3";
    let t11;
    if ($[27] !== t) {
      t11 = t(_temp2);
      $[27] = t;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== t11) {
      t12 = <div className="text-label-2 text-primary">{t11}</div>;
      $[29] = t11;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    let t13;
    if ($[31] !== t) {
      t13 = t(_temp3);
      $[31] = t;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] !== t13) {
      t14 = <div className="text-body-2 text-tertiary">{t13}</div>;
      $[33] = t13;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    if ($[35] !== t12 || $[36] !== t14) {
      t9 = <div className="flex flex-col gap-1">{t12}{t14}</div>;
      $[35] = t12;
      $[36] = t14;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    t6 = "flex flex-col gap-4 p-3 border border-tertiary rounded-md";
    if ($[38] !== t || $[39] !== vaultDocumentAccess) {
      t7 = vaultDocumentAccess && (vaultDocumentAccess.available || vaultDocumentAccess.enabled) && <Toggle checked={vaultDocumentAccess.enabled && vaultDocumentAccess.available} disabled={true} label={t(_temp4)} hint={vaultDocumentAccess.available ? t(_temp5) : t(_temp6)} />;
      $[38] = t;
      $[39] = vaultDocumentAccess;
      $[40] = t7;
    } else {
      t7 = $[40];
    }
    t4 = "flex flex-col gap-4";
    t5 = fields.map((field, index) => {
      const input_0 = watchedInputs?.[index];
      if (!input_0) {
        return null;
      }
      const declared = declaredByKey.get(input_0.key);
      const isOptional = agentManaged && declared?.isRequired === false;
      return <div key={field.id} className="flex flex-col gap-1"><div className="flex items-center gap-3"><div className="flex-1 min-w-0 flex items-center gap-2 min-h-8">{agentManaged ? <><span className="text-label-3 text-primary font-mono! truncate" title={input_0.key}>{input_0.key}</span>{declared && <div className="shrink-0 flex"><TypeChip label={isOptional ? t(_temp7, {
                  type: schemaTypeLabel(declared.schema)
                }) : schemaTypeLabel(declared.schema)} canEdit={false} /></div>}</> : <Form.Input name={`inputs.${index}.key`} aria-label={t(_temp8)} disabled={!canEditInputSchema} size="compact" placeholder={t(_temp9)} rules={{
              required: t(_temp10),
              pattern: {
                value: /^\S*$/,
                message: t(_temp11)
              }
            }} />}</div><div className="flex-1 min-w-0"><Controller name={`inputs.${index}.expression`} control={control} rules={{
              required: isOptional ? false : t(_temp12)
            }} render={t15 => {
              const {
                field: expressionField,
                fieldState
              } = t15;
              const errorMessage = fieldState.error?.message;
              return <div className="flex flex-col gap-1"><ExpandableExpressionEditor value={expressionField.value ?? ""} onChange={expressionField.onChange} autocompleteItems={autocompleteItems} languageDefinition={languageDefinition} disabled={!canEdit || expressionField.disabled} placeholder={isOptional ? t(_temp13) : t(_temp14)} hasError={!!errorMessage} labels={{
                  expand: t(_temp15),
                  dialogTitle: t(_temp16),
                  subtitle: <CelNodeFormSubtitle i18nKey={_temp17} />,
                  celSubtitle: t(_temp18),
                  instructions: t(_temp19)
                }} />{errorMessage && <span className="text-error text-caption-2">{errorMessage}</span>}</div>;
            }} /></div>{canEditInputSchema && <div className="shrink-0 flex h-8 items-center"><IconButton icon={IcoTrash16} size="compact" variant="ghost" destructive={true} onClick={() => remove(index)} aria-label="Delete property" /></div>}</div>{agentManaged ? !!input_0.comment && <span className="text-body-3 text-secondary">{input_0.comment}</span> : <Form.Input name={`inputs.${index}.comment`} aria-label={t(_temp20)} disabled={!canEditInputSchema} size="compact" placeholder={t(_temp21)} />}</div>;
    });
    $[5] = agentManaged;
    $[6] = append;
    $[7] = autocompleteItems;
    $[8] = canEdit;
    $[9] = canEditInputSchema;
    $[10] = control;
    $[11] = declaredInputs;
    $[12] = fields;
    $[13] = languageDefinition;
    $[14] = remove;
    $[15] = t;
    $[16] = vaultDocumentAccess;
    $[17] = watchedInputs;
    $[18] = handleAddInput;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    handleAddInput = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t10;
  if ($[41] !== t4 || $[42] !== t5) {
    t10 = <div className={t4}>{t5}</div>;
    $[41] = t4;
    $[42] = t5;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== canEditInputSchema || $[45] !== handleAddInput || $[46] !== t) {
    t11 = canEditInputSchema && <div><Button fullWidth={false} variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} onClick={handleAddInput}>{t(_temp22)}</Button></div>;
    $[44] = canEditInputSchema;
    $[45] = handleAddInput;
    $[46] = t;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t6 || $[51] !== t7) {
    t12 = <div className={t6}>{t7}{t10}{t11}</div>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t6;
    $[51] = t7;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== t12 || $[54] !== t8 || $[55] !== t9) {
    t13 = <div className={t8}>{t9}{t12}</div>;
    $[53] = t12;
    $[54] = t8;
    $[55] = t9;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  return t13;
};
export default InputData;
function _temp(input) {
  return [input.fieldName, input];
}
function _temp2($) {
  return $.title;
}
function _temp3($_0) {
  return $_0.subtitle;
}
function _temp4($_1) {
  return $_1.vaultDocuments.label;
}
function _temp5($_2) {
  return $_2.vaultDocuments.hint;
}
function _temp6($_3) {
  return $_3.vaultDocuments.unavailable;
}
function _temp7($_4) {
  return $_4.type.optional;
}
function _temp8($_5) {
  return $_5.key.label;
}
function _temp9($_6) {
  return $_6.key.placeholder;
}
function _temp10($_7) {
  return $_7.key.required;
}
function _temp11($_8) {
  return $_8.key.noSpaces;
}
function _temp12($_9) {
  return $_9.expression.required;
}
function _temp13($_10) {
  return $_10.expression.optionalPlaceholder;
}
function _temp14($_11) {
  return $_11.expression.placeholder;
}
function _temp15($_12) {
  return $_12.expression.expand;
}
function _temp16($_13) {
  return $_13.expression.label;
}
function _temp17($_14) {
  return $_14.aiDecisionNode.inputData.expression.subtitle;
}
function _temp18($_15) {
  return $_15.expression.celSubtitle;
}
function _temp19($_16) {
  return $_16.expression.instructions;
}
function _temp20($_17) {
  return $_17.comment.label;
}
function _temp21($_18) {
  return $_18.comment.placeholder;
}
function _temp22($_19) {
  return $_19.addInput;
}